import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/subscriptions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptions(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load subscriptions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (sub) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/subscriptions/${sub.id}`,
        { isActive: !sub.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Subscription ${sub.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleDeleteClick = (sub) => {
    setSubscriptionToDelete(sub);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/subscriptions/${subscriptionToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription deleted');
      setShowDeleteModal(false);
      setSubscriptionToDelete(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const getStatusInfo = (sub) => {
    const now = new Date();
    const expiry = new Date(sub.expiryDate);
    if (!sub.isActive) return { label: 'Inactive', color: '#6b7280', bg: '#f3f4f6' };
    if (sub.paymentStatus !== 'completed') return { label: 'Unpaid', color: '#f59e0b', bg: '#fffbeb' };
    if (expiry < now) return { label: 'Expired', color: '#ef4444', bg: '#fef2f2' };
    return { label: 'Active', color: '#10b981', bg: '#f0fdf4' };
  };

  const daysRemaining = (expiryDate) => {
    const diff = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1" style={{ fontWeight: '600' }}>Subscriptions</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage annual subscriptions for organizations</p>
        </div>
      </div>

      {/* Stats */}
      <div className="row mb-4">
        {[
          { label: 'Total', value: subscriptions.length, color: '#667eea', icon: 'bi-calendar-check' },
          { label: 'Active', value: subscriptions.filter(s => s.isActive && s.paymentStatus === 'completed' && new Date(s.expiryDate) > new Date()).length, color: '#10b981', icon: 'bi-check-circle' },
          { label: 'Expired', value: subscriptions.filter(s => new Date(s.expiryDate) <= new Date()).length, color: '#ef4444', icon: 'bi-x-circle' },
          { label: 'Inactive', value: subscriptions.filter(s => !s.isActive).length, color: '#6b7280', icon: 'bi-pause-circle' },
        ].map((s, i) => (
          <div key={i} className="col-6 col-lg-3 mb-3">
            <div className="card h-100" style={{ border: 'none', borderLeft: `4px solid ${s.color}` }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                    <div className="h4 mb-0 mt-1" style={{ fontWeight: '700' }}>{s.value}</div>
                  </div>
                  <i className={`bi ${s.icon}`} style={{ fontSize: '1.75rem', color: s.color, opacity: 0.25 }}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Subscriptions Table */}
      <div className="card" style={{ border: 'none' }}>
        <div className="card-body p-0">
          {subscriptions.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-calendar-check" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No subscriptions yet. Assign subscriptions from the Edit Organization page.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: '0.875rem' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Organization</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Package</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Price</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Start</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Expiry</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Days Left</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Status</th>
                    <th style={{ fontWeight: '600', color: '#4b5563', padding: '0.75rem 1rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => {
                    const status = getStatusInfo(sub);
                    return (
                      <tr key={sub.id}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>
                          {sub.organization?.name || 'Unknown'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {sub.pricingPackage?.name || '—'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {sub.pricingPackage ? formatCurrency(sub.pricingPackage.price, sub.pricingPackage.currency) : '—'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>{formatDate(sub.startDate)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{formatDate(sub.expiryDate)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ fontWeight: '600', color: daysRemaining(sub.expiryDate) <= 30 ? '#ef4444' : '#10b981' }}>
                            {daysRemaining(sub.expiryDate)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span className="badge" style={{ backgroundColor: status.bg, color: status.color, fontSize: '0.75rem', fontWeight: '600' }}>
                            {status.label}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div className="d-flex gap-1">
                            <button
                              onClick={() => handleToggleActive(sub)}
                              className="btn btn-sm btn-link p-0"
                              title={sub.isActive ? 'Deactivate' : 'Activate'}
                              style={{ color: sub.isActive ? '#f59e0b' : '#10b981', fontSize: '1rem' }}
                            >
                              <i className={`bi ${sub.isActive ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(sub)}
                              className="btn btn-sm btn-link p-0"
                              title="Delete"
                              style={{ color: '#ef4444', fontSize: '1rem' }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Subscription"
        message={`Are you sure you want to delete the subscription for "${subscriptionToDelete?.organization?.name}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setShowDeleteModal(false); setSubscriptionToDelete(null); }}
        confirmText="Delete"
        confirmClass="btn-danger"
      />
    </div>
  );
};

export default Subscriptions;
